import React, { Fragment, useEffect, useState } from "react";
import { createReactTableColumnHelper } from "../../../components/src/react-table/features";
import { gql, useQuery, useMutation } from "urql";
import { Link, useParams, useLocation, useSearchParams } from "wouter";
import { sortBy } from "lodash";
import { cloneDeep } from "lodash";
import { Ban, RotateCcw, TriangleAlert, Check } from "lucide-react";

import { Panel } from "../../../components/src/common/Panel";
import { twoDecPriceFormat } from "../../../common/src/utils/FormatFunctions";
import { DateForm } from "../../../components/src/dateForm";

import { YesNoModal } from "../../../components/src/YesNoModal";
import { dateFormater, serializeSwedenDate } from "../../../components/src/dateFormater";
import { MiniTransactionForm } from "./MiniTransactionForm";
import { CorporateActionForm } from "./CorporateActionForm";
import { useQueryState } from "../../../components/src/use-query-state";
import { CorporateAction, CorporateActionStatusEnum, PermissionAssetEnum } from "../types.generated";
import { GET_ME } from "../common/queries";
import { ICommandBarMenuItem } from "../../../components/src/common/CommandBar";
import { ReactDataGrid } from "../../../components/src/ReactDataGrid";


const GET_CORPORATE_ACTIONS = gql`
    query corporateActions($filter: CorporateActionFilterInput) {
        corporateActions(filter: $filter) {
            _id
            clientId
            client {
                _id
                name
            }
            recordDate
            exDate
            paymentDate
            instrumentId
            type
            instrument {
                _id
                name
                isin
            }
            currency
            amount
            foreignTax
            messageType
            corporateActionEvent
            status
            text
            externalId
            error
            correspondingTransactionId
            correspondingTransaction {
                _id
                status
            }
            updateTimestamp
            reportIds
            swiftId
            name
        }
    }
`;

const UPSERT_CORPORATE_ACTIONS = gql`
    mutation upsertCorporateActions($input: [CorporateActionInput!]!) {
        upsertCorporateActions(input: $input) {
            _id
            status
        }
    }
`;

const corporateActionGroups = [
    CorporateActionStatusEnum.Pending,
    CorporateActionStatusEnum.Preliminary,
    CorporateActionStatusEnum.Waiting,
    CorporateActionStatusEnum.Confirmed,
    CorporateActionStatusEnum.Settled
];

const groupByStatus = (corporateActions: CorporateAction[], corporateActionGroups: CorporateActionStatusEnum[]) => {
    const result: Record<string, CorporateAction[]> = {};
    corporateActionGroups.forEach((status) => {
        result[status] = [];
    });

    corporateActions = sortBy(corporateActions, "exDate").reverse();
    corporateActions.forEach((corporateAction) => {
        if (corporateActionGroups.includes(corporateAction.status)) {
            result[corporateAction.status].push(corporateAction);
        }
    });
    const optimalCorporateActionGroups = cloneDeep(corporateActionGroups);
    const optimalResult = cloneDeep(result);
    const emptyStatuses = [];
    for (const key in result) {
        if (result[key].length === 0) {
            delete optimalResult[key];
            emptyStatuses.push(key);
        }
    }
    for (let i = optimalCorporateActionGroups.length - 1; i >= 0; i--) {
        const status = optimalCorporateActionGroups[i];
        if (emptyStatuses.includes(status)) {
            optimalCorporateActionGroups.splice(i, 1);
        }
    }
    return { optimalResult, optimalCorporateActionGroups };
};

export const CorporateActionsUpdateGrid = ({ items, onStatusUpdated }): React.ReactElement => {
    const [queryArgs] = useSearchParams();
    const [selected, setSelected] = useState(null);
    const [modal, setModal] = useState({ showModal: false, payload: null });

    const [stateUpdateCorporateAction, updateCorporateActionStatus] = useMutation(UPSERT_CORPORATE_ACTIONS);

    useEffect(() => {
        if (stateUpdateCorporateAction && stateUpdateCorporateAction.data && !stateUpdateCorporateAction.error) {
            if (onStatusUpdated) {
                onStatusUpdated();
            }
            setSelected(null);
        }
    }, [onStatusUpdated, stateUpdateCorporateAction]);

    const columnHelper = createReactTableColumnHelper<any>();

    const columns = [
        columnHelper.accessor("clientName", {
            header: "Client",
            cell: (cellProps) => {
                return cellProps.row.original.client && cellProps.row.original.client._id ? (
                    <Link to={`/parties/${cellProps.row.original.client._id}`}>{cellProps.row.original.client.name}</Link>
                ) : null;
            }
        }),
        columnHelper.accessor("externalId", {
            header: "Event ref",
            cell: (cellProps) => {
                const value = cellProps.getValue();
                const args = queryArgs.toString() ? `?${queryArgs.toString()}` : "";
                return cellProps.row.original._id ? (
                    <Link to={`/corporateactions/corporateaction/${cellProps.row.original._id}${args}`}>{value}</Link>
                ) : (
                    <span>{value}</span>
                );
            }
        }),
        columnHelper.accessor("status", {
            header: "Status",
            cell: (cellProps) => {
                const value = cellProps.getValue();
                return value;
            }
        }),
        columnHelper.accessor("type", {
            header: "Type",
            cell: (cellProps) => {
                const args = queryArgs.toString() ? `?${queryArgs.toString()}` : "";
                return cellProps.row.original._id ? (
                    <Link to={`/corporateactions/corporateaction/${cellProps.row.original._id}${args}`}>
                        {cellProps.row.original.corporateActionEvent}
                    </Link>
                ) : (
                    <span>{cellProps.row.original.corporateActionEvent}</span>
                );
            }
        }),
        columnHelper.accessor("instrumentName", {
            header: "Instrument",
            cell: (cellProps) => {
                return cellProps.row.original.instrument ? (
                    <Link to={`/instruments/${cellProps.row.original.instrument._id}`}>{cellProps.row.original.instrument.name}</Link>
                ) : null;
            }
        }),
        columnHelper.accessor("foreignTax", {
            header: "Foreign tax",
            cell: (cellProps) => {
                const value = cellProps.getValue();
                return twoDecPriceFormat(value);
            }
        }),
        columnHelper.accessor("amount", {
            header: "Amount",
            cell: (cellProps) => {
                return <div style={{ textAlign: "right" }}>{twoDecPriceFormat(cellProps.getValue())}</div>;
            }
        }),
        columnHelper.accessor("currency", {
            header: "Currency",
            cell: (cellProps) => {
                return cellProps.getValue();
            }
        }),
        columnHelper.accessor("tradeDate", {
            header: "Trade date",
            cell: (cellProps) => {
                return cellProps.row.original.exDate;
            }
        }),
        columnHelper.accessor("paymentDate", {
            header: "Value date",
            cell: (cellProps) => {
                return cellProps.getValue();
            }
        }),
        columnHelper.accessor("error", {
            header: "Error",
            cell: (cellProps) => {
                return cellProps.getValue();
            }
        }),
        columnHelper.accessor("correspondingTransactionId", {
            header: "Matched with",
            cell: (cellProps) => {
                return cellProps.row.original.correspondingTransactionId ? (
                    <Link
                        to={`/corporateactions/minitransaction/${cellProps.row.original.correspondingTransactionId}${
                            queryArgs.toString() ? `?${queryArgs.toString()}` : ""
                        }`}
                    >
                        {cellProps.row.original._id}
                    </Link>
                ) : null;
            }
        }),
        columnHelper.accessor("updateTimestamp", {
            header: "Update timestamp",
            cell: (cellProps) => {
                return dateFormater(cellProps.row.original.updateTimestamp);
            }
        })
    ];

    const commands: ICommandBarMenuItem[] = [
        {
            itemKey: "newItem",
            text: "Change selection status",
            subMenuProps: {
                items: [
                    {
                        itemKey: "pending",
                        text: "Change to " + CorporateActionStatusEnum.Pending,
                        icon: <RotateCcw />,
                        onClick: () => {
                            updateStatus(selected, CorporateActionStatusEnum.Pending);
                        }
                    },
                    {
                        itemKey: "preliminary",
                        text: "Change to " + CorporateActionStatusEnum.Preliminary,
                        icon: <TriangleAlert />,
                        onClick: () => {
                            updateStatus(selected, CorporateActionStatusEnum.Preliminary);
                        }
                    },
                    {
                        itemKey: "confirmed",
                        text: "Change to " + CorporateActionStatusEnum.Confirmed,
                        icon: <Check />,
                        onClick: () => {
                            updateStatus(selected, CorporateActionStatusEnum.Confirmed);
                        }
                    },
                    {
                        itemKey: "settled",
                        text: "Change to " + CorporateActionStatusEnum.Settled,
                        onClick: () => {
                            updateStatus(selected, CorporateActionStatusEnum.Settled);
                        }
                    },
                    {
                        itemKey: "deleted",
                        text: "Change to " + CorporateActionStatusEnum.Deleted,
                        icon: <Ban />,
                        onClick: () => {
                            setModal({
                                showModal: true,
                                payload: { newStatus: CorporateActionStatusEnum.Deleted, ids: selected }
                            });
                        }
                    }
                ]
            }
        }
    ];

    const onSelectedChange = (e) => {
        setSelected(e.map((i) => i._id));
    };

    const updateStatus = async (itemIds: string[], newStatus: CorporateActionStatusEnum) => {
        const allUpdates = itemIds.map(
            async (id: string) =>
                await updateCorporateActionStatus({
                    input: { _id: id, status: newStatus }
                })
        );
        await Promise.all(allUpdates);
    };

    return (
        <div>
            {modal.showModal ? (
                <YesNoModal
                    warningText={"Are you sure you want to delete selected corporate actions?"}
                    modal={{
                        showModal: modal.showModal,
                        payload: modal.payload
                    }}
                    setModal={setModal}
                    onYes={() => {
                        updateStatus(modal.payload.ids, CorporateActionStatusEnum.Deleted);
                    }}
                />
            ) : null}

            <ReactDataGrid
                key={items}
                columns={columns}
                data={items}
                commands={selected && selected.length > 0 ? commands : []}
                defaultHiddenColumns={null}
                exportToXlsxFile={null}
                onSelectedChange={onSelectedChange}
            />
        </div>
    );
};

export const CorporateActionsByStatus = ({ exDateStart }: { exDateStart: string }): React.ReactElement => {
    const recordDate = serializeSwedenDate(new Date(exDateStart).setDate(new Date(exDateStart).getDate() + 1));

    // All corporateActions except deleted
    const filterAll = {
        statusIn: [
            CorporateActionStatusEnum.Pending,
            CorporateActionStatusEnum.Preliminary,
            CorporateActionStatusEnum.Waiting,
            CorporateActionStatusEnum.Confirmed,
            CorporateActionStatusEnum.Settled
        ],
        recordDateStart: recordDate
    };
    const [{ fetching: loading, error, data: dataCorporateActions }, refetch] = useQuery({
        query: GET_CORPORATE_ACTIONS,
        variables: { filter: filterAll },
        requestPolicy: "network-only"
        //pollInterval: 5000
    });

    if (loading) return <p>Loading corporate actions</p>;
    if (error) return <p>error: {JSON.stringify(error, null, 2)}</p>;

    const allCorporateActions = dataCorporateActions.corporateActions as CorporateAction[];
    const { optimalResult, optimalCorporateActionGroups } = groupByStatus(allCorporateActions, corporateActionGroups);

    return (
        <div>
            {optimalCorporateActionGroups.map((status) => (
                <div key={status} className="mt-4">
                    <h2>{status}</h2>
                    <CorporateActionsUpdateGrid items={optimalResult[status]} onStatusUpdated={refetch} />
                </div>
            ))}
        </div>
    );
};

export function CorporateActionsPage(): React.ReactElement {
    const params = useParams<{ id: string; type: string }>();
    const id = params.id;
    const type = params.type;
    const [exDateStart] = useQueryState("exDateStart", serializeSwedenDate(new Date(new Date().getFullYear(), 0, 1)));

    const [{ fetching: loadingMe, error: errorMe, data }] = useQuery({
        query: GET_ME,
        requestPolicy: "network-only"
    });

    const [location, navigate] = useLocation();

    if (loadingMe) return <div>Loading...</div>;

    if (errorMe)
        return (
            <div>
                <p>Error:</p>
                <pre> {JSON.stringify(errorMe, null, 2)}</pre>
            </div>
        );
    return (
        <Fragment>
            <div className="flex flex-wrap -mx-2">
                <div className="flex w-full md:w-4/12 px-2 mb-4">
                    <DateForm defaultDateString={exDateStart} dateName={"exDateStart"}></DateForm>
                </div>
                <div className="w-full md:w-8/12 px-2 mb-4 text-right">
                    {data &&
                    data.me &&
                    data.me.frontendRole &&
                    data.me.frontendRole.assets &&
                    (data.me.frontendRole.assets.includes(PermissionAssetEnum.BackOffice) || data.me.frontendRole.assets.length === 0) ? (
                        <a
                            href="https://github.com/CaptorAB/documents/blob/master/BackOffice/manual/coac_management.md"
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            Manual
                        </a>
                    ) : null}
                </div>
                <div className="flex flex-wrap -mx-2">
                    <div className={"w-full px-2 mb-4"}>
                        <CorporateActionsByStatus exDateStart={exDateStart} />
                    </div>
                </div>

                {id && type === "minitransaction" ? (
                    <Panel
                        isOpen={id != null}
                        onDismiss={() => {
                            const [, search = ""] = location.split("?");
                            navigate(`/corporateactions?${search}`);
                        }}
                    >
                        <MiniTransactionForm id={id} type={null} showHeader />
                    </Panel>
                ) : null}
                {id && type === "corporateaction" ? (
                    <Panel
                        isOpen={id != null}
                        onDismiss={() => {
                            const [, search = ""] = location.split("?");
                            navigate(`/corporateactions?${search}`);
                        }}
                        title={"Corporate action"}
                    >
                        <CorporateActionForm id={id} tradeDate={exDateStart} />
                    </Panel>
                ) : null}
            </div>
        </Fragment>
    );
}
